import QueryString from 'qs'
import z from 'zod'

import { LOCALE } from '@/legacy-src/constants/locale.constants'

import { fetchWithRetry } from '@/src/utils/fetchWithRetry'

import { strapiFetchRetryOptions, type StrapiContentType } from '@/src/domains/strapi/constants'
import type { StrapiQueryParams } from '@/src/domains/strapi/types'

export interface FetchStrapiParams {
  contentType: `${StrapiContentType}`
  documentId?: string | number
  query?: StrapiQueryParams
  cache?: {
    param?: RequestInit['cache']
    revalidate?: NextFetchRequestConfig['revalidate']
    tags?: NextFetchRequestConfig['tags']
  }
  signal?: RequestInit['signal']
  useLogger?: boolean
}

const BasicStrapiDataSchema = z.object({
  data: z.unknown(),
  meta: z.unknown().optional(),
})

export async function fetchStrapi(params: FetchStrapiParams): Promise<unknown>

export async function fetchStrapi<Schema extends z.ZodSchema>(
  params: FetchStrapiParams & { schema: Schema },
): Promise<z.infer<Schema>>

export async function fetchStrapi<Schema extends z.ZodSchema>({
  contentType,
  documentId,
  query,
  schema,
  cache,
  signal,
  useLogger = false,
}: FetchStrapiParams & { schema?: Schema }) {
  if (useLogger) {
    console.log({
      message: '🚀_STRAPI_FETCH_REQUEST',
      contentType,
      documentId,
      query,
      cache,
    })
  }

  const contentPath = documentId ? `${contentType}/${documentId}` : contentType

  const url = `${process.env.NEXT_PUBLIC_CMS_URL}/api/${contentPath}?${QueryString.stringify({
    locale: LOCALE,
    ...query,
  })}`

  const res = await fetchWithRetry(
    url,
    {
      cache: cache?.param,
      signal,
      next: {
        revalidate: cache?.revalidate,
        tags: cache?.tags,
      },
    },
    strapiFetchRetryOptions,
  )

  if (!res.ok) {
    const responseText = await res.text()

    const errorMessage = {
      message: '🚀_STRAPI_FETCH_ERROR',
      status: res.status,
      statusText: res.statusText,
      contentType,
      documentId,
      query,
      responseText,
    }

    if (useLogger) {
      console.error(errorMessage)
    }

    throw new Error(JSON.stringify(errorMessage))
  }

  const { data, meta } = BasicStrapiDataSchema.parse(await res.json())

  if (useLogger) {
    console.log({
      message: '🚀_STRAPI_FETCH_RESPONSE',
      data,
      meta,
      contentType,
      documentId,
      query,
      cache,
    })
  }

  return schema?.parse(data) ?? data
}
